import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { QUESTIONS } from "@/lib/intakeData";
import { toast } from "sonner";

const TIMES = [
  "Morning (8am – 12pm CT)",
  "Afternoon (12pm – 5pm CT)",
  "Evening (5pm – 8pm CT)",
  "Any time",
];

export default function IntakeForm() {
  const [step, setStep] = useState(0); // 0..6 questions, 7 contact, 8 success, 9 decline
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({
    full_name: "", email: "", phone: "", company: "", callback_time: "",
  });
  const [aiAssessing, setAiAssessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // After landing /?submitted=1, surface a toast and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("submitted") === "1") {
      const score = params.get("score");
      toast.success(
        score
          ? `Assessment received — viability ${score}/10. Reza will follow up within one business day.`
          : "Assessment received. Reza will follow up within one business day."
      );
      const url = new URL(window.location.href);
      url.searchParams.delete("submitted");
      url.searchParams.delete("score");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
  }, []);

  const totalSegs = 8; // 7 questions + contact
  const segs = Array.from({ length: totalSegs }, (_, i) => i <= step);

  const pickOption = (key, val, isLast) => {
    setAnswers((p) => ({ ...p, [key]: val }));

    // Texas no → decline immediately
    if (key === "is_texas" && val === "no") {
      setStep(9);
      return;
    }
    // Free-consult-only budget → decline
    if (key === "budget" && val === "I am looking for a free consultation or minimal upfront cost") {
      setStep(9);
      return;
    }
    setTimeout(() => setStep((s) => Math.min(s + 1, 6)), 180);
    if (isLast) { /* handled by setTimeout */ }
  };

  const assess = () => {
    if (!answers.description || answers.description.trim().length < 10) {
      setError("Please describe your matter (at least 10 characters).");
      return;
    }
    setError("");
    setStep(7);
  };

  const submit = async () => {
    if (!contact.full_name || !contact.email || !contact.phone) {
      setError("Name, email, and phone are required.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const payload = { ...answers, ...contact };
      const r = await api.post("/cases", payload);
      // Route back to the main page with a one-time success flag so the
      // landing surfaces a toast and we don't keep the intake card open.
      window.location.assign(`/?submitted=1&score=${r.data.ai_score}`);
    } catch (e) {
      setError(formatErr(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="intake-card fadeup fu3" id="intake" data-testid="intake-card">
      <div className="intake-eyebrow">Confidential Case Assessment</div>
      <h2>Tell us about your matter.</h2>
      <p className="lead">We'll assess it and identify the Texas firm best suited to represent you.</p>

      <div className="prog" aria-hidden>
        {segs.map((on, i) => <div key={i} className={`p-seg ${on ? "on" : ""}`} />)}
      </div>

      {error && (
        <div data-testid="intake-error" style={{ marginBottom: 12, color: "#b73b2a", fontSize: 12, fontFamily: "var(--mono)", letterSpacing: ".04em" }}>{error}</div>
      )}

      {/* QUESTION STEPS */}
      {step <= 5 && (
        <div data-testid={`intake-step-${step}`}>
          <div className="q-label">{QUESTIONS[step].label}</div>
          <div className="q-text">{QUESTIONS[step].text}</div>
          {QUESTIONS[step].sub && <div className="q-sub">{QUESTIONS[step].sub}</div>}
          <div className="opts">
            {QUESTIONS[step].options.map((o) => {
              const sel = answers[QUESTIONS[step].key] === o.value;
              return (
                <div
                  key={o.value}
                  className={`opt ${sel ? "sel" : ""}`}
                  onClick={() => pickOption(QUESTIONS[step].key, o.value, step === 5)}
                  data-testid={`intake-opt-${QUESTIONS[step].key}-${o.value.slice(0, 30).replace(/\W+/g, "-")}`}
                >
                  <div className="opt-bullet" />
                  <div>{o.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Free-text + assess */}
      {step === 6 && (
        <div data-testid="intake-step-6">
          <div className="q-label">Step 7 of 7</div>
          <div className="q-text">Briefly describe what happened and who is involved.</div>
          <div className="q-sub">Include the other party (company name if known), what they did or failed to do, and the impact on your business. The more specific you are, the more accurately we can identify the right firm for your matter. This is confidential.</div>
          <textarea
            data-testid="intake-description"
            value={answers.description || ""}
            onChange={(e) => setAnswers((p) => ({ ...p, description: e.target.value }))}
            placeholder="Example: Our Houston-based manufacturing company entered a $900,000 supply agreement with Gulf Coast Materials LLC. They delivered defective components for six months, causing us to miss three major client deadlines. We have the signed supply agreement, all delivery records, and documented client losses..."
            style={{ width: "100%", minHeight: 130, padding: "12px 14px", border: "1px solid var(--rule)", background: "#fff", fontFamily: "var(--body)", fontSize: 13.5, color: "var(--ink)", resize: "vertical", lineHeight: 1.65, borderRadius: 2, outline: "none" }}
          />
          <button
            className="btn-copper"
            style={{ marginTop: 12 }}
            onClick={assess}
            disabled={aiAssessing}
            data-testid="intake-assess-btn"
          >
            {aiAssessing ? <><Loader2 size={14} className="animate-spin" /> Assessing...</> : "Assess My Matter →"}
          </button>
          {aiAssessing && (
            <div className="ai-load">
              <Loader2 size={14} />
              Assessing matter and identifying firm match...
            </div>
          )}
        </div>
      )}

      {/* CONTACT */}
      {step === 7 && (
        <div data-testid="intake-step-contact">
          <div className="q-text" style={{ marginBottom: 14, fontSize: 18 }}>Your matter has been assessed. A Texas attorney will review it and follow up within one business day.</div>

          <div className="frow">
            <label>Full name *</label>
            <input data-testid="intake-name" value={contact.full_name} onChange={(e) => setContact((p) => ({ ...p, full_name: e.target.value }))} placeholder="Your full name" />
          </div>
          <div className="frow">
            <label>Email address *</label>
            <input data-testid="intake-email" type="email" value={contact.email} onChange={(e) => setContact((p) => ({ ...p, email: e.target.value }))} placeholder="your@company.com" />
          </div>
          <div className="frow">
            <label>Callback number *</label>
            <input data-testid="intake-phone" type="tel" value={contact.phone} onChange={(e) => setContact((p) => ({ ...p, phone: e.target.value }))} placeholder="(___) ___-____" />
          </div>
          <div className="frow">
            <label>Company name</label>
            <input data-testid="intake-company" value={contact.company} onChange={(e) => setContact((p) => ({ ...p, company: e.target.value }))} placeholder="Company (optional)" />
          </div>
          <div className="frow">
            <label>Best time to call you back</label>
            <select data-testid="intake-callback" value={contact.callback_time} onChange={(e) => setContact((p) => ({ ...p, callback_time: e.target.value }))}>
              <option value="">Select preference</option>
              {TIMES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>

          <button className="btn-copper" onClick={submit} disabled={submitting} data-testid="intake-submit-btn" style={{ width: "100%" }}>
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting...</> : "Submit for Firm Matching →"}
          </button>

          <div className="f-disc">By submitting, you consent to be contacted regarding your matter. This intake does not create an attorney-client relationship. TexasBusinessDispute.com is operated by Reza Chawla, lead attorney at Prefcap Law (Houston, Texas). If your matter is referred to another firm, a referral fee will be paid out of the attorney fees earned by the receiving firm; the specific percentage and the division of fees will be disclosed to you in the client engagement letter for your matter, and your written consent is required before any referral is finalized. Your information is treated as confidential.</div>
        </div>
      )}

      {/* DECLINE */}
      {step === 9 && (
        <div className="end-st" data-testid="intake-decline">
          <div className="end-icon" style={{ fontSize: 22, color: "var(--muted)" }}>→</div>
          <h3 style={{ fontSize: 19 }}>A different resource may serve you better</h3>
          <p>Based on your responses, the firms in our network may not be the best fit for your situation. Please see the resource below.</p>
          <a className="btn-copper" style={{ marginTop: 16, textDecoration: "none" }} href="https://www.texasbar.com/lrs" target="_blank" rel="noopener noreferrer">Texas Bar Lawyer Referral Service →</a>
        </div>
      )}
    </div>
  );
}
